import React, { MutableRefObject, ReactElement, useContext, useEffect, useRef, useState } from 'react'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import { ArtData } from '@/database/art'
import DetailsPanel from '@/components/details/details/DetailsPanel'
import EmptyPanel from '@/components/details/empty/EmptyPanel'
import Claim from '@/components/boxes/Claim'
import Bid from '@/components/boxes/Bid'
import VotesClaimsPanel from '@/components/details/votesclaims/VotesClaimsPanel'
import TokenomicsPanel from '@/components/details/tokenomics/TokenomicsPanel'
import CountdownTimer from '@/components/auction/CountdownTimer'
import ModalButtons from '@/components/ModalButtons'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import Box from '@/components/boxes/Box'
import { crazyAmount, priceText, transferFeeText } from '@/utils/formatting'
import ChatPanel from '@/components/panels/ChatPanel'
import ClaimsReceivedTooltip from '@/components/tooltips/ClaimsReceivedTooltip'
import VotesTooltip from '@/components/tooltips/VotesTooltip'
import { Context } from '@/components/Context'
import { Transaction } from '@/database/transaction'
import { ArtState, TransactionType } from '@/database/types'
import ClaimsMadeTooltip from '@/components/tooltips/ClaimsMadeTooltip'
import LoadImageButtonOnDemand from '@/components/LoadImageButtonOnDemand'
import HistoryPanel from '@/components/details/history/HistoryPanel'
import { SpecificUpdate, UpdateSpecific, UpdateSpecificType } from '@/types/websocket'
import WalletsPanel from '@/components/details/wallets/WalletsPanel'

const TABS = 7

const calculateSlidesPerView = () => {
    const screenWidth = window.innerWidth
    if (screenWidth >= 714) return TABS
    // Adjust the scaling logic below as needed
    return Math.max(1, Math.floor(screenWidth / 108)) // Example scaling logic
}

const tabName = '#auction'

const ModelAuctionCardDetails = ({ artData, onClose }: { artData: ArtData; onClose: () => void }): ReactElement => {
    const { currentAccount, transactionTokenArray, setActiveSpecificUpdates } = useContext(Context)
    const modalRef: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)
    const [activeIndex, setActiveIndex] = useState(0)
    const [slidesPerView, setSlidesPerView] = useState(7)
    const [previousBid, setPreviousBid] = useState(0)

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
    }, [])

    useEffect(() => {
        const handleResize = () => {
            const slidesPerViewNeeded = calculateSlidesPerView()
            if (slidesPerViewNeeded !== slidesPerView) {
                setSlidesPerView(slidesPerViewNeeded)
            }
        }
        handleResize()
        window.addEventListener('resize', handleResize, false)

        return () => {
            window.removeEventListener('resize', handleResize, false)
        }
    }, [artData])

    useEffect(() => {
        if (artData) {
            setActiveSpecificUpdates({
                updates: [
                    {
                        type: UpdateSpecificType.TRANSACTION_TOKEN,
                        id: artData.tokenId,
                    } as UpdateSpecific,
                ],
            } as SpecificUpdate)
        }
    }, [artData])

    useEffect(() => {
        if (!artData.tokenId) {
            return
        }
        const transactions = transactionTokenArray[artData.tokenId]
        if (transactions && artData) {
            const previousTransaction = transactions.find((transaction: Transaction) => {
                return (
                    transaction.sender === currentAccount &&
                    transaction.type === TransactionType.BID &&
                    transaction.createdAt >= artData.art.auctions?.[0]?.auctionStart
                )
            })
            if (previousTransaction) {
                setPreviousBid(parseFloat(previousTransaction.amount))
            } else {
                setPreviousBid(0)
            }
        }
    }, [transactionTokenArray, artData, currentAccount])

    return (
        <div ref={modalRef}>
            {artData && (
                <div>
                    <div className="buttons-container">
                        <ModalButtons artData={artData} onClose={onClose} />
                    </div>
                    <div className="p-copy">
                        <Bid artData={artData} previousBid={previousBid} />
                        <Claim artData={artData} />
                    </div>
                    {artData && (
                        <div className="header-container center">
                            <div key={'auction-details' + artData.art.id} className="artwork-box">
                                <div className={'header-container'}>
                                    <Box
                                        caption={'NFT'}
                                        text={'#' + (artData.tokenId ? artData.tokenId : artData.art.id)}
                                    />
                                    <Box caption={'Title'} text={artData.art.title} />
                                </div>
                                <div className={'header-container'}>
                                    <Box caption={'Description'} text={artData.art.prompt} />
                                </div>
                                <div className={'header-container bottom'}>
                                    {artData.state !== ArtState.FORCED_AUCTION && (
                                        <Box
                                            caption={'Sale Price'}
                                            styleCaption={'gradient-text bold large-bold '}
                                            text={priceText(artData, 'large-bold')}
                                            styleText={'center nav__voted-amount large'}
                                            triggerAnimation={false}
                                        />
                                    )}
                                    <Box
                                        caption={'Highest Bid'}
                                        text={crazyAmount(artData.highestBid, `white large-bold`)}
                                        textValue={artData.highestBid}
                                        styleCaption={'gradient-text bold large-bold '}
                                        styleText={'center nav__voted-amount large'}
                                    />
                                </div>
                                <LoadImageButtonOnDemand artData={artData} />
                                <div className="header-container bottom">
                                    <Box caption={'Claim Power'} text={artData.claimPower.toFixed(2)} />
                                    <Box
                                        caption={'Claims made'}
                                        id={'claims-made' + artData.art.id}
                                        text={artData.totalClaims.toFixed(2)}
                                    />
                                    <Box
                                        caption={'Claims received'}
                                        id={'claims-received' + artData.art.id}
                                        text={artData.totalClaimed.toFixed(2)}
                                    />
                                </div>
                                <div className={'header-container bottom'}>
                                    <Box
                                        caption={'Transfer Fee'}
                                        text={transferFeeText(artData)}
                                        textValue={artData?.art.transferFee}
                                    />
                                    <CountdownTimer auctionDate={artData.art.auctions?.[0]?.auctionEnd} />
                                </div>
                                <VotesTooltip artData={artData} />
                                <ClaimsMadeTooltip artData={artData} />
                                <ClaimsReceivedTooltip artData={artData} />
                            </div>
                        </div>
                    )}
                    <Tabs defaultFocus={false} selectedIndex={activeIndex} onSelect={() => {}}>
                        <Swiper
                            slidesPerView={slidesPerView}
                            spaceBetween={0}
                            freeMode={true}
                            onClick={(index) => {
                                setActiveIndex(index.clickedIndex)
                            }}
                            modules={[FreeMode, Navigation]}
                            navigation={slidesPerView < TABS}
                        >
                            <TabList>
                                <SwiperSlide className="no-border">
                                    <Tab>Your Comments</Tab>
                                </SwiperSlide>
                                <SwiperSlide className="no-border">
                                    {' '}
                                    <Tab>Details</Tab>
                                </SwiperSlide>
                                <SwiperSlide className="no-border">
                                    {' '}
                                    <Tab>Tokenomics</Tab>
                                </SwiperSlide>
                                <SwiperSlide className="no-border">
                                    {' '}
                                    <Tab>Wallets</Tab>
                                </SwiperSlide>
                                <SwiperSlide className="no-border">
                                    {' '}
                                    <Tab>Votes/Claims</Tab>
                                </SwiperSlide>
                                <SwiperSlide className="no-border">
                                    {' '}
                                    <Tab>History</Tab>
                                </SwiperSlide>
                                <SwiperSlide className="no-border">
                                    {' '}
                                    <Tab>Activity</Tab>
                                </SwiperSlide>
                            </TabList>
                        </Swiper>
                        <div>
                            <TabPanel>
                                <ChatPanel artData={artData} tabName={tabName} />
                            </TabPanel>
                            <TabPanel>
                                <DetailsPanel artData={artData} tabName={tabName} />
                            </TabPanel>
                            <TabPanel>
                                <TokenomicsPanel artData={artData} tabName={tabName} />
                            </TabPanel>
                            <TabPanel>
                                <WalletsPanel artData={artData} tabName={tabName} />
                            </TabPanel>
                            <TabPanel>
                                <VotesClaimsPanel artData={artData} tabName={tabName} />
                            </TabPanel>
                            <TabPanel>
                                <HistoryPanel artData={artData} tabName={tabName} />
                            </TabPanel>
                            <TabPanel>
                                <EmptyPanel artData={artData} />
                            </TabPanel>
                        </div>
                    </Tabs>
                </div>
            )}
        </div>
    )
}

export default ModelAuctionCardDetails
